/**
 * ============================================================================
 * CACHE MANAGER - TTL-Based In-Memory Cache with LocalStorage Fallback
 * ============================================================================
 * 
 * Features:
 * - Time-to-live (TTL) based expiration
 * - Manual cache invalidation
 * - LocalStorage backup for persistence across sessions
 * - Automatic cleanup of expired entries
 * - Console logging for debugging
 * 
 * ============================================================================
 */

const DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes default
const CACHE_VERSION = '1.0'; // For cache invalidation when structure changes

class CacheManager {
  constructor() {
    this.cache = new Map();
    this.loadFromLocalStorage();
    // Cleanup expired entries every minute
    setInterval(() => this.cleanup(), 60000);
  }

  /**
   * Generate cache key with version
   */
  _getCacheKey(key) {
    return `cache_v${CACHE_VERSION}_${key}`;
  }

  /**
   * Load cache from localStorage on initialization
   */
  loadFromLocalStorage() {
    try {
      const keys = Object.keys(localStorage);
      const cacheKeys = keys.filter(k => k.startsWith(`cache_v${CACHE_VERSION}_`));
      
      cacheKeys.forEach(key => {
        const data = localStorage.getItem(key);
        if (data) {
          const parsed = JSON.parse(data);
          const originalKey = key.replace(`cache_v${CACHE_VERSION}_`, '');
          
          // Only load if not expired
          if (parsed.expiresAt > Date.now()) {
            this.cache.set(originalKey, parsed);
            console.log(`📦 [Cache] Loaded from localStorage: ${originalKey}`);
          } else {
            localStorage.removeItem(key);
            console.log(`🗑️ [Cache] Removed expired entry from localStorage: ${originalKey}`);
          }
        }
      });
    } catch (error) {
      console.error('❌ [Cache] Error loading from localStorage:', error);
    }
  }

  /**
   * Save to localStorage
   */
  _saveToLocalStorage(key, entry) {
    try {
      const cacheKey = this._getCacheKey(key);
      localStorage.setItem(cacheKey, JSON.stringify(entry));
    } catch (error) {
      // LocalStorage might be full or disabled
      console.warn('⚠️ [Cache] Could not save to localStorage:', error);
    }
  }

  /**
   * Get data from cache
   * @param {string} key - Cache key
   * @returns {any|null} - Cached data or null if expired/not found
   */
  get(key) {
    // First check in-memory cache
    let entry = this.cache.get(key);
    
    // If not in memory, try localStorage as fallback
    if (!entry) {
      try {
        const cacheKey = this._getCacheKey(key);
        const stored = localStorage.getItem(cacheKey);
        if (stored) {
          entry = JSON.parse(stored);
          // Check if expired
          if (entry.expiresAt > Date.now()) {
            // Restore to in-memory cache
            this.cache.set(key, entry);
            console.log(`📦 [Cache] Restored from localStorage: ${key}`);
          } else {
            // Remove expired entry
            localStorage.removeItem(cacheKey);
            console.log(`⏰ [Cache] Miss: ${key} - Expired (from localStorage)`);
            return null;
          }
        } else {
          console.log(`❌ [Cache] Miss: ${key} - Not found`);
          return null;
        }
      } catch (error) {
        console.error(`❌ [Cache] Error reading from localStorage:`, error);
        return null;
      }
    }
    
    // Check if expired
    if (entry.expiresAt < Date.now()) {
      console.log(`⏰ [Cache] Miss: ${key} - Expired`);
      this.delete(key);
      return null;
    }

    console.log(`✅ [Cache] Hit: ${key} - Age: ${Math.round((Date.now() - entry.timestamp) / 1000)}s`);
    return entry.data;
  }

  /**
   * Set data in cache with TTL
   * @param {string} key - Cache key
   * @param {any} data - Data to cache
   * @param {number} ttl - Time to live in milliseconds (default: 5 minutes)
   */
  set(key, data, ttl = DEFAULT_TTL) {
    const entry = {
      data,
      timestamp: Date.now(),
      expiresAt: Date.now() + ttl,
      ttl
    };

    this.cache.set(key, entry);
    this._saveToLocalStorage(key, entry);
    
    console.log(`💾 [Cache] Set: ${key} - TTL: ${ttl / 1000}s`);
  }

  /**
   * Delete specific cache entry
   * @param {string} key - Cache key to delete
   */
  delete(key) {
    this.cache.delete(key);
    const cacheKey = this._getCacheKey(key);
    localStorage.removeItem(cacheKey);
    console.log(`🗑️ [Cache] Deleted: ${key}`);
  }

  /**
   * Delete all cache entries matching a pattern
   * @param {string} pattern - String pattern to match (e.g., 'members', 'tasks')
   */
  invalidatePattern(pattern) {
    let count = 0;
    for (const key of this.cache.keys()) {
      if (key.includes(pattern)) {
        this.delete(key);
        count++;
      }
    }
    console.log(`🧹 [Cache] Invalidated ${count} entries matching pattern: ${pattern}`);
  }

  /**
   * Clear all cache
   */
  clear() {
    const size = this.cache.size;
    this.cache.clear();
    
    // Clear from localStorage
    const keys = Object.keys(localStorage);
    const cacheKeys = keys.filter(k => k.startsWith(`cache_v${CACHE_VERSION}_`));
    cacheKeys.forEach(k => localStorage.removeItem(k));
    
    console.log(`🧹 [Cache] Cleared all (${size} entries)`);
  }

  /**
   * Clean up expired entries
   */
  cleanup() {
    const now = Date.now();
    let cleaned = 0;
    
    for (const [key, entry] of this.cache.entries()) {
      if (entry.expiresAt < now) {
        this.delete(key);
        cleaned++;
      }
    }
    
    if (cleaned > 0) {
      console.log(`🧹 [Cache] Cleanup: Removed ${cleaned} expired entries`);
    }
  }

  /**
   * Get cache statistics
   */
  getStats() {
    const now = Date.now();
    const entries = Array.from(this.cache.entries());
    
    return {
      total: entries.length,
      expired: entries.filter(([_, e]) => e.expiresAt < now).length,
      valid: entries.filter(([_, e]) => e.expiresAt >= now).length,
      keys: Array.from(this.cache.keys())
    };
  }

  /**
   * Check if cache has valid entry
   */
  has(key) {
    return this.get(key) !== null;
  }
}

// Singleton instance
const cacheManager = new CacheManager();

export default cacheManager;
